import { useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import {
  Box,
  Heading,
  Icon,
  List,
  Paragraph,
  Switch,
  TextButton,
  Toast,
  UpsellBanner,
  useToast,
} from '@zillow/constellation-10';
import { IconTagDollarSignFilled } from '@zillow/constellation-icons';
import { useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../selectors/useSelector';

import PropertiesActions from '../../actions/PropertiesActions';

import accessUtils from '../../utils/accessUtils';

import SingleFamilyCSEvents from '../../model/ClickstreamEvent/TotalMonthlyPriceWithFee/SingleFamilyCSEvents';

const SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert = () => {
  const { enqueueToast } = useToast();

  const dispatch = useDispatch();

  const {
    appConfig,
    cookieEnabledServices,
    currentProperty,
    displayTotalMonthlyPriceWithFee,
    listingAlias,
    modularListingProgressStep,
    user,
  } = useSelector(
    (state) => ({
      appConfig: state?.app?.appConfig,
      cookieEnabledServices: state?.app?.cookieEnabledServices,
      currentProperty: state?.properties?.currentProperty,
      displayTotalMonthlyPriceWithFee: state.properties.currentProperty.displayTotalMonthlyPriceWithFee,
      listingAlias: state.properties.currentProperty.listingAlias,
      modularListingProgressStep: state.properties.currentProperty.modularListingProgress?.step,
      user: state?.user,
    }),
    shallowEqual,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee } =
    useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee();

  const canAccessUpdatedTotalMonthlyPriceCopy = accessUtils.canAccessUpdatedTotalMonthlyPriceCopy({
    appConfig,
    cookieEnabledServices,
  });

  const onClickSwitchHandler = async (
    event: React.MouseEvent<HTMLInputElement, MouseEvent> & { target: { checked: boolean } },
  ) => {
    const valueToBe = event.target.checked;

    setIsSubmitting(true);

    if (valueToBe) {
      SingleFamilyCSEvents.OptInOffToOn({
        currentProperty,
        user,
        triggerLocation: modularListingProgressStep?.includes('review') ? 'posting_path_review' : 'posting_path',
      });
    } else {
      SingleFamilyCSEvents.OptInOnToOff({
        currentProperty,
        user,
        triggerLocation: modularListingProgressStep?.includes('review') ? 'posting_path_review' : 'posting_path',
      });
    }

    try {
      await setDisplayTotalMonthlyPriceWithFee({
        getParams: { listingAlias, displayTotalMonthlyPriceWithFee: valueToBe },
      });

      dispatch(PropertiesActions.updateCurrentPropertyDisplayTotalMonthlyPriceWithFee(valueToBe));
    } catch (e) {
      enqueueToast(<Toast tone="critical">This setting could not be saved. Try again.</Toast>);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        data-testid="SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert"
        css={{
          marginBottom: '400',
          border: '1px solid {colors.border.subtle}',
          borderRadius: '{radii.obj.default}',
          overflow: 'hidden',
        }}
      >
        <UpsellBanner.Root tone="soft" style={{ alignItems: 'start', justifyContent: 'space-between' }}>
          <UpsellBanner.Media>
            <Icon render={<Icon render={<IconTagDollarSignFilled />} />} />
          </UpsellBanner.Media>

          <UpsellBanner.Content>
            <UpsellBanner.Header>
              <Box css={{ display: 'flex', flexDirection: 'row', gap: '{spacing.default}' }}>
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Heading level={6}>
                    Show the {canAccessUpdatedTotalMonthlyPriceCopy ? 'total monthly price' : 'total price'} on your
                    listing
                  </Heading>

                  <Paragraph>
                    Turn this on if:
                    <List.Root appearance="bullet">
                      <List.Item css={{ paddingBlock: 0 }}>
                        Your base rent already includes all required monthly fees, or
                      </List.Item>

                      <List.Item css={{ paddingBlock: 0 }}>
                        You&apos;re adding all required monthly fees here — we&apos;ll add them to the base rent to show
                        one {canAccessUpdatedTotalMonthlyPriceCopy ? 'total monthly price' : 'total price'}
                      </List.Item>
                    </List.Root>
                    <TextButton
                      textStyle="body-bold"
                      asChild
                      onClick={() => {
                        SingleFamilyCSEvents.LearnMore({
                          user,
                          currentProperty,
                          triggerObject: 'costs_and_fees',
                          triggerLocation: 'posting_path',
                        });
                      }}
                      data-eventid={SingleFamilyCSEvents.LearnMore.data_event_id}
                    >
                      <a
                        target="_blank"
                        href="https://www.zillow.com/rentals-network/pricing-transparency/"
                        rel="noreferrer"
                      >
                        Learn more
                      </a>
                    </TextButton>
                  </Paragraph>
                </Box>

                <Box
                  css={{
                    display: { lg: 'none' },
                    marginRight: { base: 'calc({spacing.1000} * -1)' },
                  }}
                >
                  <div>
                    <Switch
                      data-testid="display-total-monthly-price-with-fee-switch"
                      size="md"
                      disabled={isSubmitting}
                      checked={displayTotalMonthlyPriceWithFee}
                      onClick={onClickSwitchHandler}
                      data-eventid={
                        displayTotalMonthlyPriceWithFee
                          ? SingleFamilyCSEvents.OptInOnToOff.data_event_id
                          : SingleFamilyCSEvents.OptInOffToOn.data_event_id
                      }
                    />
                  </div>
                </Box>
              </Box>
            </UpsellBanner.Header>
          </UpsellBanner.Content>

          <Box
            css={{
              display: { base: 'none', lg: 'flex' },
            }}
          >
            <UpsellBanner.Action>
              <Box css={{ marginRight: 'calc({spacing.default} * -1)' }}>
                <Switch
                  data-testid="display-total-monthly-price-with-fee-switch"
                  size="md"
                  disabled={isSubmitting}
                  checked={displayTotalMonthlyPriceWithFee}
                  onClick={onClickSwitchHandler}
                  data-eventid={
                    displayTotalMonthlyPriceWithFee
                      ? SingleFamilyCSEvents.OptInOnToOff.data_event_id
                      : SingleFamilyCSEvents.OptInOffToOn.data_event_id
                  }
                />
              </Box>
            </UpsellBanner.Action>
          </Box>
        </UpsellBanner.Root>
      </Box>
    </>
  );
};

export default SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert;
